import { memo, useCallback, useEffect, useReducer, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import * as Toast from '@radix-ui/react-toast';
import {
  defaultToastDurationMs,
  registerToastHandler,
  unregisterToastHandler,
  type ToastOptions,
  type ToastVariant,
} from './toastApi.js';
import { ToastViewport, type ToastRecord } from './ToastViewport.js';

/** Spec Part 9 §14: max 3 visible; additional enqueue. */
export const MAX_VISIBLE_TOASTS = 3;

/**
 * Spec Part 9 §14: pending queue cap — when full, drop oldest pending
 * (visible toasts are not sliced away to make room for newcomers).
 */
export const MAX_PENDING_TOASTS = 20;

let toastSequence = 0;

function createToastId(): string {
  toastSequence += 1;
  return `toast-${toastSequence}`;
}

interface ToastQueueState {
  readonly visible: readonly ToastRecord[];
  readonly pending: readonly ToastRecord[];
}

type ToastQueueAction =
  | { readonly type: 'enqueue'; readonly toast: ToastRecord }
  | { readonly type: 'dismiss'; readonly id: string };

const INITIAL_QUEUE: ToastQueueState = { visible: [], pending: [] };

function replaceToastById(
  list: readonly ToastRecord[],
  toast: ToastRecord,
): readonly ToastRecord[] | null {
  const index = list.findIndex((item) => item.id === toast.id);
  if (index < 0) {
    return null;
  }
  const next = [...list];
  next[index] = toast;
  return next;
}

function toastQueueReducer(state: ToastQueueState, action: ToastQueueAction): ToastQueueState {
  if (action.type === 'enqueue') {
    const replacedVisible = replaceToastById(state.visible, action.toast);
    if (replacedVisible !== null) {
      return { ...state, visible: replacedVisible };
    }
    const replacedPending = replaceToastById(state.pending, action.toast);
    if (replacedPending !== null) {
      return { ...state, pending: replacedPending };
    }

    if (state.visible.length < MAX_VISIBLE_TOASTS) {
      return { ...state, visible: [...state.visible, action.toast] };
    }
    const merged = [...state.pending, action.toast];
    const pending =
      merged.length <= MAX_PENDING_TOASTS
        ? merged
        : merged.slice(merged.length - MAX_PENDING_TOASTS);
    return { ...state, pending };
  }

  const pendingWithout = state.pending.filter((toast) => toast.id !== action.id);
  const without = state.visible.filter((toast) => toast.id !== action.id);
  const removedVisible = without.length !== state.visible.length;
  const removedPending = pendingWithout.length !== state.pending.length;

  if (!removedVisible && !removedPending) {
    return state;
  }

  if (!removedVisible) {
    return { ...state, pending: pendingWithout };
  }

  if (pendingWithout.length === 0) {
    return { visible: without, pending: pendingWithout };
  }
  const [next, ...rest] = pendingWithout;
  if (next === undefined) {
    return { visible: without, pending: rest };
  }
  return { visible: [...without, next], pending: rest };
}

export interface ToastProviderProps {
  readonly children: ReactNode;
}

export const ToastProvider = memo<ToastProviderProps>(({ children }) => {
  const [queue, dispatch] = useReducer(toastQueueReducer, INITIAL_QUEUE);

  const dismissToast = useCallback((id: string): void => {
    dispatch({ type: 'dismiss', id });
  }, []);

  const showToast = useCallback((options: ToastOptions): void => {
    const variant: ToastVariant = options.variant ?? 'info';
    const duration = options.duration ?? defaultToastDurationMs(variant);
    dispatch({
      type: 'enqueue',
      toast: {
        id: options.id ?? createToastId(),
        message: options.message,
        variant,
        duration,
      },
    });
  }, []);

  useEffect(() => {
    registerToastHandler(showToast, dismissToast);
    return (): void => {
      unregisterToastHandler();
    };
  }, [showToast, dismissToast]);

  const viewport = (
    <ToastViewport toasts={queue.visible} onDismiss={dismissToast} />
  );

  return (
    <Toast.Provider swipeDirection="right" duration={4000}>
      {children}
      {/*
        Portal to document.body so --z-toast competes with portaled TEST/PWA
        chrome. In-tree toasts lose to body portals at the same/higher band
        (AdminTestEconomyChrome was painting over the top-right toast).
      */}
      {typeof document !== 'undefined'
        ? createPortal(viewport, document.body)
        : viewport}
    </Toast.Provider>
  );
});

ToastProvider.displayName = 'ToastProvider';
